import type { Migration } from '../api/migrations';
import SectionCard from './SectionCard';

/**
 * FD-246 / OQ-011 — the per-wave Autopilot group-tag aggregate.
 *
 * This exists to make a deferred decision answerable from evidence. The owner
 * decision (2026-08-10) passes an unmapped source tag through VERBATIM and defers
 * source→destination tag mapping to R-FD-032, gated on real engagement data. That
 * deferral is only defensible if we actually collect the data — so this panel is the
 * input, not decoration. If devices arrive untagged, or the customer pre-created
 * matching groups in the destination, the mapping table is never needed at all.
 *
 * The panel renders nothing until the broker has resolved at least one device: an
 * empty aggregate would read as "no tags found" when it means "nothing has run yet",
 * and those imply opposite conclusions about whether R-FD-032 is needed.
 */
export default function AutopilotTagSummaryPanel({ migration }: { migration: Migration }) {
  const summary = migration.autopilotTagSummary;
  if (!summary) return null;

  const resolved =
    summary.bySource.source + summary.bySource.campaign + summary.bySource.none + summary.bySource.unknown;
  if (resolved === 0) return null;

  return (
    <SectionCard>
      <h3 className="mb-3 text-sm font-medium text-zinc-900">Autopilot group tags</h3>
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 text-sm">
        <Stat label="from source" value={summary.bySource.source} />
        <Stat label="campaign default" value={summary.bySource.campaign} />
        <Stat label="no tag" value={summary.bySource.none} />
        {/* A read failure is a fault, not a category — styled so it does not sit
            visually level with the three legitimate outcomes. */}
        {summary.unknownCount > 0 ? (
          <span className="font-medium text-red-600">
            <span className="font-mono">{summary.unknownCount}</span> tag read failed
          </span>
        ) : null}
        {summary.bySource.notResolved > 0 ? (
          <Stat label="not yet resolved" value={summary.bySource.notResolved} muted />
        ) : null}
      </div>

      {summary.sourceTags.length > 0 ? (
        <div className="mt-3">
          <p className="mb-1.5 text-xs text-zinc-500">
            {summary.distinctTagCount} distinct source{' '}
            {summary.distinctTagCount === 1 ? 'tag' : 'tags'} seen in this wave. These pass through
            verbatim — they are applied as-is in the destination tenant.
          </p>
          <ul className="flex flex-wrap gap-1.5">
            {summary.sourceTags.map((t: { tag: string; count: number }) => (
              <li
                key={t.tag}
                className="rounded border border-zinc-200 bg-zinc-50 px-2 py-0.5 font-mono text-xs text-zinc-700"
              >
                {t.tag} <span className="text-zinc-400">×{t.count}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </SectionCard>
  );
}

function Stat({ label, value, muted }: { label: string; value: number; muted?: boolean }) {
  return (
    <span className={muted ? 'text-zinc-500' : 'text-zinc-700'}>
      <span className="font-mono font-medium text-zinc-900">{value}</span> {label}
    </span>
  );
}
